import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllIndustries, industryComparison } from '../data/industries.js';
import CTA from './CTA';
import OurCommitment from './OurCommitment';
import { generateCTAMessage } from '../utils/ctaMessages.js';

const IndustryComparison = () => {
  const [selectedIndustries, setSelectedIndustries] = useState(['healthcare', 'education']);
  const [scrollProgress, setScrollProgress] = useState(0);
  const industries = getAllIndustries();

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setScrollProgress(scrollPercent);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Note: Additional enhanced data and color configs were removed as unused to improve clarity

  const toggleIndustry = (industryId) => {
    setSelectedIndustries(prev => {
      if (prev.includes(industryId)) {
        return prev.filter(id => id !== industryId);
      } else if (prev.length < 3) {
        return [...prev, industryId];
      }
      return prev;
    });
  };

  const getComparisonData = () => {
    return selectedIndustries.map(id => {
      const industry = industries.find(ind => ind.id === id);
      return {
        ...industry,
        comparisonMetrics: industryComparison.data[id] || [0, 0, 0, 0]
      };
    });
  };

  const comparisonData = getComparisonData();

  return (
    <>
      {/* Scroll Progress Indicator */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: `${scrollProgress}%`,
        height: '3px',
        background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
        zIndex: 9999,
        transition: 'width 0.1s ease-out'
      }}></div>

      {/* Breadcrumb Navigation - Mobile Responsive */}
      <div style={{
        position: 'fixed',
        top: 'clamp(60px, 10vw, 65px)',
        left: 0,
        right: 0,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(14, 165, 233, 0.1)',
        padding: 'clamp(0.75rem, 2vw, 1rem) 0',
        zIndex: 999,
        transition: 'all 0.3s ease'
      }}>
        <div className="container">
          <nav style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Link to="/" style={{
              color: '#64748b',
              textDecoration: 'none',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#0ea5e9'}
            onMouseLeave={(e) => e.target.style.color = '#64748b'}>
              <i className="bi bi-house"></i>
              <span>Home</span>
            </Link>
            <i className="bi bi-chevron-right" style={{ color: '#cbd5e1', fontSize: '0.8rem' }}></i>
            <Link to="/industries" style={{
              color: '#64748b',
              textDecoration: 'none',
              fontSize: '0.9rem',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#0ea5e9'}
            onMouseLeave={(e) => e.target.style.color = '#64748b'}>
              Industries
            </Link>
            <i className="bi bi-chevron-right" style={{ color: '#cbd5e1', fontSize: '0.8rem' }}></i>
            <span style={{
              color: '#0ea5e9',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}>
              Industry Comparison
            </span>
          </nav>
        </div>
      </div>

      {/* Hero Section - Mobile Responsive */}
      <section id="hero" className="industry-hero-section" style={{
        paddingTop: 'clamp(110px, 18vw, 140px)' // Account for fixed header + breadcrumb height
      }}>
        <div className="hero-background" style={{ 
          background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #333333 100%)',
          position: 'relative',
          overflow: 'hidden',
          minHeight: 'clamp(70vh, 80vh, 90vh)',
          display: 'flex',
          alignItems: 'center',
          paddingBottom: 'clamp(2rem, 6vw, 4rem)'
        }}>
          {/* Background Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.06) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.04) 0%, transparent 50%)
            `,
            zIndex: 1
          }}></div>
          
          {/* Floating Particles */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            pointerEvents: 'none'
          }}>
            {[...Array(8)].map((_, i) => (
              <div key={i} style={{
                position: 'absolute',
                width: '6px',
                height: '6px',
                background: 'rgba(56, 189, 248, 0.4)',
                borderRadius: '50%',
                top: `${15 + i * 12}%`,
                left: `${8 + i * 15}%`,
                animation: `float ${4 + i * 0.3}s ease-in-out infinite`,
                animationDelay: `${i * 0.4}s`
              }}></div>
            ))}
          </div>

          <div className="container" style={{ position: 'relative', zIndex: 3 }}>
            <div className="row justify-content-center text-center">
              <div className="col-lg-10">
                <div className="hero-content">
                  {/* Industry Icon */}
                  <div className="industry-icon-large mb-4" style={{
                    display: 'inline-block',
                    background: 'rgba(30, 41, 59, 0.8)',
                    border: '2px solid rgba(14, 165, 233, 0.3)',
                    borderRadius: '50%',
                    padding: 'clamp(1rem, 3vw, 2rem)',
                    backdropFilter: 'blur(15px)',
                    boxShadow: '0 8px 25px rgba(14, 165, 233, 0.2)'
                  }}>
                    <span style={{ fontSize: 'clamp(2.5rem, 6vw, 4rem)' }}>📊</span>
                  </div>
                  
                  {/* Title and Subtitle */}
                  <h1 className="hero-title mb-3" style={{
                    fontSize: 'clamp(2rem, 4vw, 3rem)',
                    fontWeight: '800',
                    color: '#ffffff',
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                    lineHeight: '1.2',
                    marginBottom: '2rem'
                  }}>Compare Industries</h1>
                  
                  <h2 className="hero-subtitle mb-4" style={{
                    fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: '400',
                    lineHeight: '1.4',
                    marginBottom: '2rem'
                  }}>See how our AI solutions adapt across different industries</h2>
                  
                  {/* Overview */}
                  <p className="hero-description mb-5" style={{
                    fontSize: 'clamp(1rem, 2.2vw, 1.1rem)',
                    color: 'rgba(255, 255, 255, 0.8)',
                    maxWidth: 'clamp(300px, 80vw, 800px)',
                    margin: '0 auto 3rem',
                    lineHeight: '1.6'
                  }}>Compare AI adoption rates, ROI potential, and implementation timelines across different sectors to make informed decisions for your organization.</p>
                  
                  {/* CTA Buttons */}
                  <div className="hero-cta" style={{
                    display: 'flex',
                    gap: 'clamp(1rem, 3vw, 1.5rem)',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    marginBottom: '2rem'
                  }}>
                    <Link to="/contact" style={{
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                      border: 'none',
                      color: '#ffffff',
                      padding: 'clamp(1rem, 3vw, 1.4rem) clamp(2rem, 6vw, 3.5rem)',
                      borderRadius: 'clamp(40px, 8vw, 60px)',
                      fontWeight: '700',
                      fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 'clamp(0.5rem, 1.5vw, 0.75rem)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 10px 30px rgba(14, 165, 233, 0.4), 0 6px 20px rgba(0, 0, 0, 0.1)',
                      minWidth: 'clamp(180px, 40vw, 220px)',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-5px)';
                      e.target.style.boxShadow = '0 15px 40px rgba(14, 165, 233, 0.5), 0 10px 30px rgba(0, 0, 0, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 10px 30px rgba(14, 165, 233, 0.4), 0 6px 20px rgba(0, 0, 0, 0.1)';
                    }}>
                      <i className="bi bi-rocket-takeoff"></i>
                      Start Your Project
                    </Link>
                    
                    <Link to="/case-studies" style={{
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '2px solid rgba(14, 165, 233, 0.3)',
                      color: '#ffffff',
                      padding: 'clamp(1rem, 3vw, 1.4rem) clamp(2rem, 6vw, 3.5rem)',
                      borderRadius: 'clamp(40px, 8vw, 60px)',
                      fontWeight: '700',
                      fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 'clamp(0.5rem, 1.5vw, 0.75rem)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      backdropFilter: 'blur(20px)',
                      minWidth: 'clamp(180px, 40vw, 220px)',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-5px)';
                      e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                      e.target.style.borderColor = 'rgba(14, 165, 233, 0.6)';
                      e.target.style.boxShadow = '0 15px 40px rgba(14, 165, 233, 0.2), 0 10px 30px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                      e.target.style.borderColor = 'rgba(14, 165, 233, 0.3)';
                      e.target.style.boxShadow = 'none';
                    }}>
                      <i className="bi bi-eye"></i>
                      View Case Studies
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section - Mobile Responsive */}
      <section className="industry-comparison-section py-5" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: 'clamp(3rem, 8vw, 6rem) 0'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(14, 165, 233, 0.06) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          {/* Section Header */}
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <h2 style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '900',
                marginBottom: '1rem'
              }}>
                <span style={{ color: '#000000' }}>Industry</span>{' '}
                <span style={{ 
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>Analysis</span>
              </h2>
              <p style={{
                fontSize: 'clamp(1rem, 2.2vw, 1.2rem)',
                color: '#64748b',
                lineHeight: '1.6',
                maxWidth: 'clamp(300px, 80vw, 600px)',
                margin: '0 auto'
              }}>
                Compare AI adoption rates, ROI potential, and implementation timelines across different sectors
              </p>
            </div>
          </div>

          {/* Industry Selector */}
          <div className="row justify-content-center mb-5">
            <div className="col-lg-10">
              <div style={{
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                borderRadius: 'clamp(12px, 3vw, 24px)',
                padding: 'clamp(1.5rem, 4vw, 3rem)',
                border: '1px solid rgba(14, 165, 233, 0.2)',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)'
              }}>
                <h4 style={{
                  fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
                  fontWeight: '700',
                  color: '#1e293b',
                  marginBottom: 'clamp(1rem, 3vw, 2rem)',
                  textAlign: 'center'
                }}>
                  Select Industries to Compare (Max 3)
                </h4>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 'clamp(0.75rem, 2vw, 1rem)',
                  justifyContent: 'center'
                }}>
                  {industries.map((industry) => (
                    <button
                      key={industry.id}
                      onClick={() => toggleIndustry(industry.id)}
                      style={{
                        background: selectedIndustries.includes(industry.id) 
                          ? 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)'
                          : 'rgba(255, 255, 255, 0.8)',
                        border: selectedIndustries.includes(industry.id)
                          ? '2px solid #0ea5e9'
                          : '2px solid rgba(14, 165, 233, 0.2)',
                        color: selectedIndustries.includes(industry.id) ? '#ffffff' : '#64748b',
                        padding: 'clamp(0.75rem, 2vw, 1rem) clamp(1.5rem, 4vw, 2rem)',
                        borderRadius: 'clamp(12px, 2.5vw, 16px)',
                        fontWeight: '600',
                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'clamp(0.5rem, 1.5vw, 0.75rem)',
                        boxShadow: selectedIndustries.includes(industry.id) 
                          ? '0 8px 25px rgba(14, 165, 233, 0.3)'
                          : '0 4px 15px rgba(0, 0, 0, 0.05)'
                      }}
                      onMouseEnter={(e) => {
                        if (!selectedIndustries.includes(industry.id)) {
                          e.target.style.background = 'rgba(14, 165, 233, 0.1)';
                          e.target.style.borderColor = '#0ea5e9';
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 8px 25px rgba(14, 165, 233, 0.2)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selectedIndustries.includes(industry.id)) {
                          e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                          e.target.style.borderColor = 'rgba(14, 165, 233, 0.2)';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.05)';
                        }
                      }}
                    >
                      <span style={{ fontSize: 'clamp(1.2rem, 3vw, 1.5rem)' }}>{industry.icon}</span>
                      {industry.title.split(' ')[0]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          {comparisonData.length > 0 && (
            <div className="row justify-content-center">
              <div className="col-lg-12">
                <div style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 'clamp(12px, 3vw, 24px)',
                  padding: 'clamp(1.5rem, 4vw, 3rem)',
                  border: '1px solid rgba(14, 165, 233, 0.2)',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    overflowX: 'auto',
                    borderRadius: '20px'
                  }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'separate',
                      borderSpacing: 0,
                      background: 'rgba(255, 255, 255, 0.9)'
                    }}>
                      <thead>
                        <tr>
                          <th style={{
                            padding: 'clamp(1rem, 3vw, 1.5rem)',
                            textAlign: 'left',
                            fontWeight: '700',
                            fontSize: 'clamp(0.75rem, 1.8vw, 0.85rem)',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                            color: 'white',
                            border: 'none',
                            minWidth: 'clamp(150px, 30vw, 200px)'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}>
                              <i className="bi bi-graph-up-arrow" style={{ fontSize: '1rem' }}></i>
                              Metrics
                            </div>
                          </th>
                          {comparisonData.map((industry) => (
                            <th key={industry.id} style={{
                              padding: 'clamp(1rem, 3vw, 1.5rem)',
                              textAlign: 'center',
                              fontWeight: '700',
                              fontSize: 'clamp(0.75rem, 1.8vw, 0.85rem)',
                              textTransform: 'uppercase',
                              letterSpacing: '1px',
                              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                              color: 'white',
                              border: 'none',
                              minWidth: 'clamp(150px, 30vw, 200px)'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem'
                              }}>
                                <span style={{ fontSize: 'clamp(1rem, 2.5vw, 1.2rem)' }}>{industry.icon}</span>
                                {industry.title.split(' ')[0]}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {industryComparison.metrics.map((metric, index) => (
                          <tr key={index} style={{
                            borderBottom: '1px solid rgba(14, 165, 233, 0.1)'
                          }}>
                            <td style={{
                              padding: 'clamp(1rem, 3vw, 1.5rem)',
                              fontWeight: '600',
                              color: '#1e293b',
                              fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                              border: 'none',
                              background: 'rgba(14, 165, 233, 0.05)'
                            }}>
                              {metric}
                            </td>
                            {comparisonData.map((industry) => (
                              <td key={industry.id} style={{
                                padding: 'clamp(1rem, 3vw, 1.5rem)',
                                textAlign: 'center',
                                border: 'none'
                              }}>
                                <div style={{
                                  background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(14, 165, 233, 0.05) 100%)',
                                  border: '2px solid rgba(14, 165, 233, 0.2)',
                                  borderRadius: 'clamp(8px, 2vw, 12px)',
                                  padding: 'clamp(0.75rem, 2vw, 1rem)',
                                  position: 'relative',
                                  overflow: 'hidden',
                                  transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(14, 165, 233, 0.2)';
                                  e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = 'none';
                                  e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
                                }}>
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '2px',
                                    background: 'linear-gradient(90deg, #0ea5e9, #0284c7)'
                                  }}></div>
                                  <span style={{
                                    fontWeight: '700',
                                    color: '#1e293b',
                                    fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                                    position: 'relative',
                                    zIndex: 1
                                  }}>
                                    {industry.comparisonMetrics[index]}%
                                  </span>
                                </div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Industry Cards */}
          {comparisonData.length > 0 && (
            <div className="row g-4 mt-5">
              {comparisonData.map((industry) => (
                <div key={industry.id} className="col-12 col-lg-4">
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: 'clamp(12px, 3vw, 24px)',
                    padding: 'clamp(1.5rem, 4vw, 3rem)',
                    border: '1px solid rgba(14, 165, 233, 0.2)',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 25px 50px rgba(14, 165, 233, 0.15)';
                    e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.06)';
                    e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
                  }}>
                    {/* Background Pattern */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `
                        radial-gradient(circle at 30% 20%, rgba(14, 165, 233, 0.05) 0%, transparent 50%),
                        radial-gradient(circle at 70% 80%, rgba(14, 165, 233, 0.03) 0%, transparent 50%)
                      `,
                      zIndex: 1
                    }}></div>

                    <div style={{ 
                      position: 'relative', 
                      zIndex: 2,
                      display: 'grid',
                      gridTemplateRows: 'auto auto auto auto',
                      gap: '1rem',
                      height: '100%'
                    }}>
                      {/* Row 1: Icon + Title - Combined in one line */}
                      <div style={{
                        minHeight: 'clamp(50px, 10vw, 70px)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'clamp(0.75rem, 2vw, 1rem)'
                      }}>
                        <div style={{
                          width: 'clamp(40px, 8vw, 50px)',
                          height: 'clamp(40px, 8vw, 50px)',
                          background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                          borderRadius: 'clamp(8px, 2vw, 12px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
                          boxShadow: '0 4px 15px rgba(14, 165, 233, 0.3)',
                          flexShrink: 0
                        }}>
                          {industry.icon}
                        </div>
                        <h4 style={{
                          fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                          fontWeight: '700',
                          color: '#1e293b',
                          margin: 0,
                          lineHeight: '1.3'
                        }}>
                          {industry.title}
                        </h4>
                      </div>

                      {/* Row 2: Industry Subtitle */}
                      <div style={{
                        minHeight: 'clamp(30px, 6vw, 40px)',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <p style={{
                          fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
                          color: '#64748b',
                          margin: 0,
                          lineHeight: '1.5'
                        }}>
                          {industry.subtitle}
                        </p>
                      </div>

                      {/* Row 3: Key Stats */}
                      <div style={{
                        minHeight: 'clamp(100px, 20vw, 120px)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'clamp(0.75rem, 2vw, 1rem)'
                      }}>
                        {industry.stats.slice(0, 2).map((stat, index) => (
                          <div key={index} style={{
                            background: 'rgba(14, 165, 233, 0.08)',
                            border: '1px solid rgba(14, 165, 233, 0.2)',
                            borderRadius: 'clamp(8px, 2vw, 12px)',
                            padding: 'clamp(0.75rem, 2vw, 1rem)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'all 0.3s ease',
                            minHeight: 'clamp(40px, 8vw, 50px)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(14, 165, 233, 0.12)';
                            e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(14, 165, 233, 0.08)';
                            e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
                          }}>
                            <span style={{
                              fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)',
                              color: '#64748b',
                              fontWeight: '600'
                            }}>
                              {stat.label}
                            </span>
                            <span style={{
                              fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                              color: '#0ea5e9',
                              fontWeight: '700'
                            }}>
                              {stat.number}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Row 4: CTA Button - Pushed to bottom */}
                      <div style={{ marginTop: 'auto' }}>
                        <Link 
                          to={`/industries/${industry.id}`}
                          style={{
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                            border: 'none',
                            color: '#ffffff',
                            padding: 'clamp(0.625rem, 1.5vw, 0.75rem) clamp(1.25rem, 3vw, 1.5rem)',
                            borderRadius: 'clamp(8px, 2vw, 12px)',
                            fontWeight: '700',
                            fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 'clamp(0.375rem, 1vw, 0.5rem)',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 4px 15px rgba(14, 165, 233, 0.3)',
                            width: '100%'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 8px 25px rgba(14, 165, 233, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 4px 15px rgba(14, 165, 233, 0.3)';
                          }}
                        >
                          <span>Learn More</span>
                          <i className="bi bi-arrow-right" style={{ fontSize: '0.9rem' }}></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </section>

      {/* Our Commitment Section */}
      <div id="commitment">
        <OurCommitment />
      </div>

      {/* CTA Section */}
      <div id="cta">
        <CTA {...generateCTAMessage('industry-comparison')} />
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
      `}</style>
    </>
  );
};

export default IndustryComparison; 